// Original POS bill with its full history (items, payments, void/refund stamps, item cancellations, sales, journals).
import { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Badge } from '../ui/badge';
import api from '../../lib/api';

const ts = (s) => (s ? new Date(s).toLocaleString([], { dateStyle: 'short', timeStyle: 'short' }) : '—');

export function PosVoidDetailDialog({ orderId, onOpenChange }) {
  const [d, setD] = useState(null);
  useEffect(() => {
    setD(null);
    if (orderId) api.get(`/cashier/void-history/${orderId}`).then(({ data }) => setD(data)).catch(() => setD({ error: true }));
  }, [orderId]);
  const o = d?.order || {};
  return (
    <Dialog open={!!orderId} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="pos-void-detail-dialog">
        <DialogHeader><DialogTitle>Bill #{o.order_number || '…'} <Badge variant="outline" className="ml-2">{o.status}</Badge></DialogTitle></DialogHeader>
        {d?.error && <p className="text-sm text-rose-600">Could not load this bill.</p>}
        {d && !d.error && (
          <div className="space-y-4 text-sm">
            <div className="grid grid-cols-2 gap-2 text-xs" data-testid="pos-void-detail-summary">
              <div>Created: {ts(o.created_at)} · Cashier: {o.cashier_name || '—'}</div>
              <div>Total: {o.total} · Payment: {o.payment_method || '—'}</div>
              {o.voided_at && <div className="col-span-2 text-rose-700">Voided {ts(o.voided_at)} by {o.voided_by_name || o.voided_by} · approved by {o.void_authorized_by || '—'} · from status {o.voided_from_status || '—'} · reason: {o.void_reason || '—'}</div>}
              {o.refunded_at && <div className="col-span-2 text-amber-700">Refunded {ts(o.refunded_at)} · reason: {o.refund_reason || '—'}</div>}
              <div className="col-span-2">ZATCA: {(o.zatca || {}).status || 'not submitted'}</div>
            </div>
            <Section title="Items">{(o.items || []).map((it, i) => <li key={i}>{it.quantity} × {it.name} — {it.subtotal}</li>)}</Section>
            <Section title="Removed / cancelled items">{(o.cancelled_items || []).map((c, i) => (
              <li key={i}>{ts(c.cancelled_at)} · {c.quantity} × {c.name} · {c.stage === 'before_kitchen' ? 'before kitchen' : 'after kitchen'} · by {c.cancelled_by_name || c.cancelled_by} · approved {(c.authorized_by || {}).name || '—'} · {c.reason}</li>))}</Section>
            <Section title="Credit notes">{(d.credit_notes || []).map((c) => <li key={c.id}>#{c.order_number} · {c.total} · {ts(c.created_at)} · ZATCA {(c.zatca || {}).status || '—'}</li>)}</Section>
            <Section title="Linked sales">{(d.sales || []).map((s) => <li key={s.id}>{s.id.slice(0, 8)} · {s.final_amount} {s.voided ? '· voided' : ''}</li>)}</Section>
            <Section title="Journals">{(d.journals || []).map((j) => <li key={j.id}>{j.entry_number || j.reference} · {j.total_debit} · {j.status}{j.reversal_of ? ' · reversal' : ''}</li>)}</Section>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Section({ title, children }) {
  const empty = !children || (Array.isArray(children) && children.length === 0);
  return (
    <div>
      <div className="text-xs font-semibold text-muted-foreground mb-1">{title}</div>
      {empty ? <p className="text-xs text-muted-foreground">None</p> : <ul className="text-xs space-y-0.5 list-disc pl-4">{children}</ul>}
    </div>
  );
}
